import Constants from 'client/util/Constants';
import moment from 'moment';

const {
  dateFormat,
  dateTimeFormat,
  dateTimeLongFormat,
  dateTimestampFormat,
  timeFormat,
} = Constants;

/**
 * General functions used to format and compute properties from moment date objects.
 * Useful to prevent having to write tests for functions that are used throughout status computation
 * and date formatting. This is written as a class so that we can easily test these methods by
 * replacing the date library in the tests. Now we can assume that moment js works and is fully
 * tested and simply test if we are calling the right functions.
 */
export const DateUtilClass = class {
  /**
   * Allows you to change the date libary used. Created for easy testing. Defaults to moment.
   * @param {Object} dateLibrary [the library you wish to use during the date formatting]
   */
  constructor(dateLibrary) {
    this.dateLibrary = dateLibrary || moment;
  }

  /**
   * Add specified amount of units to a given date.
   * E.g. add 90 days; or add 12 hours
   */
  add(timestamp, amount, unit = 'days') {
    return this.dateLibrary(timestamp).add(amount, unit);
  }

  /**
   * Custom date/time format
   */
  customFormatDate(timestamp, customFormat) {
    return this.dateLibrary(timestamp).format(customFormat);
  }

  /**
   * Calculates the amount of days after a specific date. Opposite of daysUntil.
   * @param {String|Object} date - date or date object to compare
   * @return {Number} The number of days that passed since the given date.
   */
  daysAfter(date) {
    const { dateLibrary } = this;
    return dateLibrary().diff(dateLibrary(date), 'days');
  }

  /**
   * Determine if a date is after a comparison date
   * @param {String|Object} date - date string or date object to compare
   * @param {String|Object} comparisonDate - comparison date string or oject
   * @return {Boolean} The result of whether the date is within the date range or not
   */
  daysRemaining(date, comparisonDate) {
    const { dateLibrary } = this;
    const initialDate = date === 'today' ? dateLibrary() : dateLibrary(date);
    const inDateRange = initialDate.diff(comparisonDate, 'days');
    return inDateRange;
  }

  /**
   * calculate the number of days left until the date is reached. A day is added to the calcuation
   * to round the amount of time up. Even if there are 0.5 days left it will return 1.
   * This is used for many functions that require a certain color based on the number of days left.
   */
  daysUntil(timestamp) {
    const { dateLibrary } = this;
    return dateLibrary(timestamp).diff(dateLibrary(), 'days') + 1;
  }

  /**
   * Returns the date a certain number of days ago
   * @param {Integer} days - The number of days ago to calcuate date
   * @return {Date} The date that occured x days ago according to the param
   */
  dateDaysAgo(days) {
    const { dateLibrary } = this;
    return dateLibrary().subtract(days, 'days');
  }

  /**
   * most common date format MM-DD-YYYY. Why just format the date and pass which ever format to
   * moment? Because we want to be explicit and not have to import the date formats from the
   * Constants file every time we want to format a date.
   */
  formatDate(timestamp) {
    return this.dateLibrary(timestamp).format(dateFormat);
  }

  /**
   * long date format MM-DD-YYYY [at] hh:mma
   */
  formatDateLong(timestamp) {
    return this.dateLibrary(timestamp).format(dateTimeLongFormat);
  }

  /**
   * date/time format MMM DD, YYYY [@] hh:mma'
   */
  formatDateTime(timestamp) {
    return this.dateLibrary(timestamp).format(dateTimeFormat);
  }

  /**
   * date/time format YYYYMMDDHHmmss'
   */
  formatDateTimestamp(timestamp) {
    return this.dateLibrary(timestamp).format(dateTimestampFormat);
  }

  formatUnixDate(timestamp) {
    return this.dateLibrary.unix(timestamp).format(dateFormat);
  }

  formatUnixTime(timestamp) {
    return this.dateLibrary.unix(timestamp).format(timeFormat);
  }

  formatUnixTimestamp(timestamp) {
    return this.dateLibrary(timestamp, dateTimeFormat).valueOf();
  }

  /**
   * calculate if a date is in the past
   */
  hasExpired(timestamp) {
    const { dateLibrary } = this;
    return dateLibrary(timestamp).isBefore(dateLibrary());
  }

  /**
   * Determine if a date is after a comparison date
   * @param {String|Object} date - date string or date object to compare
   * @param {String|Object} comparisonDate - comparison date string or object
   * @return {Boolean} The result of whether the date is after the comparison date
   */
  isAfterDate(date, comparisonDate) {
    const { dateLibrary } = this;
    const inDateRange = dateLibrary(date) > dateLibrary(comparisonDate);
    return inDateRange;
  }

  /**
   * Determine if a date is before a comparison date
   * @param {String|Object} date - date string or date object to compare
   * @param {String|Object} comparisonDate - comparison date string or oject
   * @return {Boolean} The result of whether the date is before comparison date
   */
  isBeforeDate(date, comparisonDate) {
    const { dateLibrary } = this;
    const inDateRange = dateLibrary(date) < dateLibrary(comparisonDate);
    return inDateRange;
  }

  /**
   * Determine if a date is within a certain number of days of today
   * @param {String|Object} startDate - start of range
   * @param {String|Object} endDate - end of range
   * @return {Boolean} The result of whether the date is within the date range or not
   */
  isInDateRange(startDate, endDate) {
    const { dateLibrary } = this;
    const inDateRange = dateLibrary().isBetween(startDate, endDate, 'day', '[]');
    return inDateRange;
  }

  startOfDay(timeStamp) {
    const { dateLibrary } = this;
    return dateLibrary(timeStamp).startOf('day');
  }

  /**
   * Determine if a date is before a certain number of days before today
   * @param {Object} date - The date to be checked
   * @param {Integer} days - The number of days before today
   * @return {Boolean} The result of whether the date is days before today
   */
  moreThanDaysBeforeToday(date, days) {
    const { dateLibrary } = this;
    const daysBeforeToday = dateLibrary().subtract(days, 'days');
    const comparisonDate = dateLibrary(date);
    const isDaysBefore = comparisonDate.isBefore(daysBeforeToday);
    return isDaysBefore;
  }

  /**
   * Determine if a date is within a certain  number of days of today
   * @param {String|Object} date - The date to be checked
   * @param {Integer} days - The number of days from today
   * @return {Boolean} The result of whether the date is within the date range or not
   */
  withinDayRangeOfToday(date, days) {
    const { dateLibrary } = this;
    const today = dateLibrary();
    const comparisonDate = dateLibrary(date);
    const dateDiff = today.diff(comparisonDate, 'days');
    const withinDayRange = dateDiff <= days && dateDiff >= 0;
    return withinDayRange;
  }
};

export default new DateUtilClass();
